import React, { useEffect, useState } from 'react';
import { KeyValueItem } from '@gerdau/hefesto-mais-web';
import { Title } from '@gerdau/hefesto';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { sendTagging } from '../../../../../../../store/ducks/tagging/actions';
import {
  EOrderType,
  IBilledOrderData
} from '../../../../../../../store/ducks/long-steel-orders/types';
import {
  ECustomerTypeView,
  IUserOrderItemPreference
} from '../../../../../../../store/ducks/user/types';
import {
  formatCurrency,
  formatNumberTon
} from '../../../../../../../utils/formatNumbers';
import { uppercaseOnlyFirstLetter } from '../../../../../../../utils/formatStrings';
import { Container, Link } from './styles';
import TrackingBilledOrder from './TrackingBilledOrder';

interface IExpandedBilledOrderRowContentProps {
  item: IBilledOrderData;
  userOrderPreference: IUserOrderItemPreference;
  customerViewType: ECustomerTypeView;
}

const ExpandedBilledOrderRowContent: React.FC<
  IExpandedBilledOrderRowContentProps
> = ({ item, userOrderPreference, customerViewType }) => {
  const dispatch = useDispatch();

  const getFormatedFieldValue = (value: string): string => {
    if (value !== 'Indisponível') {
      return `R$ ${formatCurrency(value)}`;
    } else {
      return ' - ';
    }
  };

  const getCanDownloadNF = (): boolean => {
    const unavailableText = 'Indisponível';
    return (
      item.conditions.unitPriceWithoutTaxes !== unavailableText &&
      item.conditions.invoiceItemValueWithoutTaxes !== unavailableText &&
      item.conditions.invoiceItemValueWithTaxes !== unavailableText &&
      item.conditions.invoiceTotalValueWithoutTaxes !== unavailableText &&
      item.conditions.invoiceTotalValueWithTaxes !== unavailableText
    );
  };

  const [canDownloadNF] = useState(getCanDownloadNF());

  useEffect(() => {
    dispatch(
      sendTagging({
        screenName: 'LG:MeusPedidos:AcosLongos:PedidosFaturados',
        itemName: 'expandable_card:ver_detalhes',
        itemDescription: 'meus_pedidos_acosLongos_pedidosFaturados_verDetalhes',
        event: 'click'
      })
    );
  }, [dispatch]);

  return (
    <>
      {item.billingHistory.orderType === EOrderType.PLANOS && (
        <TrackingBilledOrder billedOrderItem={item} />
      )}
      <Container>
        <div className="details">
          <Title level={3}>Detalhes da fatura</Title>

          <KeyValueItem title="Tipo de documento">Fatura</KeyValueItem>

          <KeyValueItem title="Volume total da nota">
            {formatNumberTon(item.conditions.invoiceVolume.replace(/,/g, ''))}T
          </KeyValueItem>

          <KeyValueItem title="Chave de acesso NF">
            {canDownloadNF ? (
              <Link
                className="nf-access-key"
                rel="noopener noreferrer"
                target="_blank"
                href={`http://www.nfe.fazenda.gov.br/portal/consultaRecaptcha.aspx?tipoConteudo=XbSeqxE8pl8=&tipoConsulta=completa&nfe=${item.nfeAccessKey}`}
              >
                {item.nfeAccessKey}
              </Link>
            ) : (
              ' - '
            )}
          </KeyValueItem>

          <KeyValueItem title="Total sem imposto">
            {getFormatedFieldValue(
              item.conditions.invoiceTotalValueWithoutTaxes
            )}
          </KeyValueItem>

          <KeyValueItem title="Total com imposto">
            {getFormatedFieldValue(item.conditions.invoiceTotalValueWithTaxes)}
          </KeyValueItem>
        </div>

        <div className="values">
          <Title level={3}>Detalhes do pedido</Title>

          <KeyValueItem title="Nº OV Gerdau">
            {item.billingHistory.salesOrder}
          </KeyValueItem>

          <KeyValueItem title="Número pedido cliente">
            {item.billingHistory.customerOrder}
          </KeyValueItem>

          {customerViewType === ECustomerTypeView.EMITTER && (
            <KeyValueItem title="Recebedor">
              {`${uppercaseOnlyFirstLetter(item.shipTo.name)} - CNPJ: ${
                item.shipTo.taxNumber
              }`}
              {`${uppercaseOnlyFirstLetter(
                item.shipTo.address
              )} - ${uppercaseOnlyFirstLetter(item.shipTo.city)}/${
                item.shipTo.state
              }`}
            </KeyValueItem>
          )}

          {(customerViewType === ECustomerTypeView.RECEIVER ||
            customerViewType === ECustomerTypeView.FABRICATION_RECEIVER) && (
            <KeyValueItem title="Emissor">
              {`${uppercaseOnlyFirstLetter(item.soldTo.name)} - CNPJ: ${
                item.soldTo.taxNumber
              }`}
              {`${uppercaseOnlyFirstLetter(
                item.soldTo.address
              )} - ${uppercaseOnlyFirstLetter(item.soldTo.city)}/${
                item.soldTo.state
              }`}
            </KeyValueItem>
          )}

          <KeyValueItem title="Nº item">
            {item.billingHistory.salesOrderItem}
          </KeyValueItem>

          <KeyValueItem title="Criação do pedido">
            {moment(item.billingHistory.itemCreationDate, 'YYYY-MM-DD').format(
              'DD/MM/YYYY'
            )}
          </KeyValueItem>

          <KeyValueItem title="Material Gerdau">
            {userOrderPreference.idProduct !== '0'
              ? item.billingHistory.sku
              : item.billingHistory.customerSKU
              ? item.billingHistory.customerSKU
              : ' - '}
          </KeyValueItem>

          <KeyValueItem title="Condição de pagamento">
            {item.conditions.paymentTermsDescription}
          </KeyValueItem>

          <KeyValueItem title="Planta">
            {item.billingHistory.plantDescription}
          </KeyValueItem>
        </div>
      </Container>
    </>
  );
};

export default ExpandedBilledOrderRowContent;
